/**
 * Deterministic anchor sweep for the exclusion-aware generation test fit.
 *
 * The shelf packer centres the packed component group on the parcel envelope.
 * When exclusion zones intersect that default position, this search translates
 * the whole packed group — internal gaps and setbacks preserved — across a
 * bounded, fixed-order grid of candidate offsets and picks the one that
 * minimises `(exclusionOverlapAcres, outsideAcres, distanceFromCentre)`
 * lexicographically. No randomness, no wall clock: identical inputs always
 * produce the identical offset, which the frozen-exhibit re-validation relies
 * on.
 */
import polygonClipping from 'polygon-clipping';

import type { GenerationTestFitRectangle } from './generationTestFit';
import {
  closedRingM,
  multiPolygonAreaM2,
  SQUARE_METRES_PER_ACRE,
  type LocalPointM,
} from './generationLayoutUsableArea';

const MIN_STEP_M = 5;
const MAX_STEPS_PER_AXIS = 6; // 6 each side of centre -> at most 13 x 13 grid
/** Interior-seeding grid resolution over the parcel bbox (<= 81 extra anchors). */
const INTERIOR_SEED_GRID_N = 9;
/** Dedupe resolution for candidate offsets, metres. */
const OFFSET_DEDUPE_M = 0.01;
const AREA_TOLERANCE_ACRES = 1e-9;

export interface PlacementOffsetM {
  dxM: number;
  dyM: number;
}

export interface SearchPlacementOffsetInput {
  rectangles: readonly GenerationTestFitRectangle[];
  /** Parcel ring in the shared local metre plane. */
  parcelPolygonM: readonly LocalPointM[];
  /** Exclusion union already clipped to the parcel, same plane. */
  exclusionGeometry: polygonClipping.MultiPolygon;
  /** Full oriented parcel envelope (not reduced by setbacks). */
  usableEnvelope: { widthM: number; heightM: number };
  /** Bounding envelope required by the packed plan including setbacks. */
  requiredEnvelope: { widthM: number; heightM: number };
}

export interface SearchPlacementOffsetResult {
  offset: PlacementOffsetM;
  evaluatedOffsets: number;
}

export function translateRectangle(
  rectangle: GenerationTestFitRectangle,
  offset: PlacementOffsetM,
): GenerationTestFitRectangle {
  if (offset.dxM === 0 && offset.dyM === 0) return rectangle;
  return {
    ...rectangle,
    xM: rectangle.xM + offset.dxM,
    yM: rectangle.yM + offset.dyM,
    polygonM: rectangle.polygonM.map(point => ({
      xM: point.xM + offset.dxM,
      yM: point.yM + offset.dyM,
    })),
  };
}

function axisOffsets(halfRangeM: number): number[] {
  if (halfRangeM <= 0) return [0];
  const steps = Math.min(
    MAX_STEPS_PER_AXIS,
    Math.floor(halfRangeM / MIN_STEP_M),
  );
  if (steps <= 0) return [0];
  const stepM = halfRangeM / steps;
  const offsets = [0];
  for (let index = 1; index <= steps; index += 1) {
    offsets.push(index * stepM, -index * stepM);
  }
  return offsets;
}

function translateMultiPolygon(
  geometry: polygonClipping.MultiPolygon,
  offset: PlacementOffsetM,
): polygonClipping.MultiPolygon {
  return geometry.map(polygon => polygon.map(ring => ring.map(
    ([x, y]) => [x + offset.dxM, y + offset.dyM] as [number, number],
  )));
}

function pointInLocalRing(
  xM: number,
  yM: number,
  ring: readonly LocalPointM[],
): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!;
    const b = ring[j]!;
    const intersects = (a.yM > yM) !== (b.yM > yM)
      && xM < (b.xM - a.xM) * (yM - a.yM) / (b.yM - a.yM) + a.xM;
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Anchor candidates seeded from the parcel polygon's INTERIOR. The slack grid
 * alone is centred on the oriented bbox, whose centre can fall outside a
 * concave parcel entirely (L-shapes, river frontage arms); seeding a fixed
 * grid of in-polygon points steers the pack toward real ground. Offsets are
 * clamped to the slack range so the translated pack always stays inside the
 * oriented envelope. Deterministic: fixed grid, fixed order.
 */
function interiorSeedOffsets(
  parcelPolygonM: readonly LocalPointM[],
  halfSlackXM: number,
  halfSlackYM: number,
): PlacementOffsetM[] {
  if (parcelPolygonM.length < 3) return [];
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of parcelPolygonM) {
    minX = Math.min(minX, point.xM);
    maxX = Math.max(maxX, point.xM);
    minY = Math.min(minY, point.yM);
    maxY = Math.max(maxY, point.yM);
  }
  const offsets: PlacementOffsetM[] = [];
  for (let row = 0; row < INTERIOR_SEED_GRID_N; row += 1) {
    for (let column = 0; column < INTERIOR_SEED_GRID_N; column += 1) {
      const xM = minX + (maxX - minX) * (column + 0.5) / INTERIOR_SEED_GRID_N;
      const yM = minY + (maxY - minY) * (row + 0.5) / INTERIOR_SEED_GRID_N;
      if (!pointInLocalRing(xM, yM, parcelPolygonM)) continue;
      offsets.push({
        dxM: Math.max(-halfSlackXM, Math.min(halfSlackXM, xM)),
        dyM: Math.max(-halfSlackYM, Math.min(halfSlackYM, yM)),
      });
    }
  }
  return offsets;
}

/**
 * Find the packed-group offset minimising exclusion overlap. Only meaningful
 * for plans that fit the envelope; overflowing plans keep the centred anchor.
 */
export function searchPlacementOffset(
  input: SearchPlacementOffsetInput,
): SearchPlacementOffsetResult {
  const centred: SearchPlacementOffsetResult = {
    offset: { dxM: 0, dyM: 0 },
    evaluatedOffsets: 0,
  };
  if (input.rectangles.length === 0 || input.exclusionGeometry.length === 0) {
    return centred;
  }

  // The packed group may translate until its required envelope touches the
  // usable envelope; slack is symmetric because the packer centres the group.
  const halfSlackXM = Math.max(0, (input.usableEnvelope.widthM - input.requiredEnvelope.widthM) / 2);
  const halfSlackYM = Math.max(0, (input.usableEnvelope.heightM - input.requiredEnvelope.heightM) / 2);

  let groupGeometry: polygonClipping.MultiPolygon;
  let parcelGeometry: polygonClipping.Polygon;
  try {
    let merged: polygonClipping.MultiPolygon = polygonClipping.union(
      [closedRingM(input.rectangles[0]!.polygonM)] as polygonClipping.Polygon,
    );
    for (let index = 1; index < input.rectangles.length; index += 1) {
      merged = polygonClipping.union(
        merged,
        [closedRingM(input.rectangles[index]!.polygonM)] as polygonClipping.Polygon,
      );
    }
    groupGeometry = merged;
    parcelGeometry = [closedRingM(input.parcelPolygonM)] as polygonClipping.Polygon;
  } catch {
    return centred;
  }
  const groupAreaAcres = multiPolygonAreaM2(groupGeometry) / SQUARE_METRES_PER_ACRE;

  // Candidate offsets: the centre-out slack grid plus parcel-interior seeds
  // (concave parcels can have their bbox centre entirely off the buildable
  // ground). Deduped on a 1 cm lattice, ordered centre-out so the first
  // perfect candidate is also the nearest-to-centre one and the scan can
  // stop immediately.
  const seen = new Set<string>();
  const candidates: PlacementOffsetM[] = [];
  const pushCandidate = (offset: PlacementOffsetM): void => {
    const key = `${Math.round(offset.dxM / OFFSET_DEDUPE_M)}:${Math.round(offset.dyM / OFFSET_DEDUPE_M)}`;
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push(offset);
  };
  for (const dyM of axisOffsets(halfSlackYM)) {
    for (const dxM of axisOffsets(halfSlackXM)) {
      pushCandidate({ dxM, dyM });
    }
  }
  for (const offset of interiorSeedOffsets(input.parcelPolygonM, halfSlackXM, halfSlackYM)) {
    pushCandidate(offset);
  }
  candidates.sort((left, right) => (
    (left.dxM * left.dxM + left.dyM * left.dyM)
      - (right.dxM * right.dxM + right.dyM * right.dyM)
  ) || left.dyM - right.dyM || left.dxM - right.dxM);

  let best: { offset: PlacementOffsetM; overlapAcres: number; outsideAcres: number } | null = null;
  let evaluated = 0;
  const perfect = (): boolean => best != null
    && best.overlapAcres <= AREA_TOLERANCE_ACRES
    && best.outsideAcres <= AREA_TOLERANCE_ACRES;
  const evaluateOffsets = (offsets: readonly PlacementOffsetM[]): void => {
    for (const offset of offsets) {
      let overlapAcres: number;
      let outsideAcres: number;
      try {
        const translated = translateMultiPolygon(groupGeometry, offset);
        overlapAcres = multiPolygonAreaM2(
          polygonClipping.intersection(translated, input.exclusionGeometry),
        ) / SQUARE_METRES_PER_ACRE;
        const containedAcres = multiPolygonAreaM2(
          polygonClipping.intersection(translated, parcelGeometry),
        ) / SQUARE_METRES_PER_ACRE;
        outsideAcres = Math.max(0, groupAreaAcres - containedAcres);
      } catch {
        continue;
      }
      evaluated += 1;
      if (
        best === null
        || overlapAcres < best.overlapAcres - AREA_TOLERANCE_ACRES
        || (Math.abs(overlapAcres - best.overlapAcres) <= AREA_TOLERANCE_ACRES
          && outsideAcres < best.outsideAcres - AREA_TOLERANCE_ACRES)
      ) {
        best = { offset, overlapAcres, outsideAcres };
      }
      if (perfect()) return;
    }
  };

  evaluateOffsets(candidates);

  // Local refinement: the coarse grid/seed step can straddle a narrow parcel
  // arm entirely, so sweep a finer lattice around the best coarse candidate.
  // Same dedupe set, same deterministic centre-out ordering.
  if (best !== null && !perfect()) {
    const anchor = (best as { offset: PlacementOffsetM }).offset;
    const coarseStepXM = Math.max(MIN_STEP_M, halfSlackXM / MAX_STEPS_PER_AXIS);
    const coarseStepYM = Math.max(MIN_STEP_M, halfSlackYM / MAX_STEPS_PER_AXIS);
    const refined: PlacementOffsetM[] = [];
    const seenBefore = seen.size;
    for (let row = -MAX_STEPS_PER_AXIS; row <= MAX_STEPS_PER_AXIS; row += 1) {
      for (let column = -MAX_STEPS_PER_AXIS; column <= MAX_STEPS_PER_AXIS; column += 1) {
        const offset: PlacementOffsetM = {
          dxM: Math.max(-halfSlackXM, Math.min(
            halfSlackXM,
            anchor.dxM + column * coarseStepXM / MAX_STEPS_PER_AXIS,
          )),
          dyM: Math.max(-halfSlackYM, Math.min(
            halfSlackYM,
            anchor.dyM + row * coarseStepYM / MAX_STEPS_PER_AXIS,
          )),
        };
        const key = `${Math.round(offset.dxM / OFFSET_DEDUPE_M)}:${Math.round(offset.dyM / OFFSET_DEDUPE_M)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        refined.push(offset);
      }
    }
    if (seen.size > seenBefore) {
      refined.sort((left, right) => (
        (left.dxM - anchor.dxM) ** 2 + (left.dyM - anchor.dyM) ** 2
          - ((right.dxM - anchor.dxM) ** 2 + (right.dyM - anchor.dyM) ** 2)
      ) || left.dyM - right.dyM || left.dxM - right.dxM);
      evaluateOffsets(refined);
    }
  }

  return best
    ? { offset: (best as { offset: PlacementOffsetM }).offset, evaluatedOffsets: evaluated }
    : { ...centred, evaluatedOffsets: evaluated };
}
